// External
import React, { Suspense, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { Switch, Route, Redirect } from 'react-router-dom';
import { CompatRouter } from 'react-router-dom-v5-compat';
import { ConnectedRouter } from 'connected-react-router';
import { SecureRoute, Security, LoginCallback } from '@okta/okta-react';
import { LastLocationProvider } from 'react-router-last-location';
import { OktaAuth, toRelativeUrl } from '@okta/okta-auth-js';

// Internal
import WithTranslations from '../intl/Intl';
import HomePage from '../user/homePage/HomePage';
import appHistory from '../util/history';
import { routes } from './router/routes';
import { routerPropTypes } from './constants';
import { Spinner } from '~COMPONENTS/';
import { getIsShoppingEnabled } from '../gamedayAccount/selectors';

// Constants
const {
  VITE_OAUTH_CLIENT_ID,
  VITE_OAUTH_RESPONSE_TYPE,
  VITE_OAUTH_CALLBACK_URL,
  VITE_OAUTH_BASE_URL,
  VITE_OAUTH_ISSUER,
  VITE_OAUTH_REDIRECT_URL,
  VITE_OAUTH_LOGIN_URL,
  OKTA_OAUTH_BASE_URL = null, // if this comes from build, then needs VITE_
  OKTA_OAUTH_ISSUER_URL = null // if this comes from build, then needs VITE_
} = import.meta.env;
const oAuthDefault = {
  clientId: VITE_OAUTH_CLIENT_ID,
  response_type: VITE_OAUTH_RESPONSE_TYPE,
  callbackUrl: VITE_OAUTH_CALLBACK_URL,
  redirectUrl: VITE_OAUTH_REDIRECT_URL,
  loginUrl: VITE_OAUTH_LOGIN_URL,
  redirectUri:
    import.meta.env.MODE === 'prod'
      ? 'https://gameday.nike.net'
      : typeof window !== 'undefined' && window.location.origin,
  baseUrl: OKTA_OAUTH_BASE_URL || VITE_OAUTH_BASE_URL,
  issuer: OKTA_OAUTH_ISSUER_URL || VITE_OAUTH_ISSUER
};

const Routes = ({ component, history = appHistory, oAuth, isShoppingEnabled }) => {
  const [isCheckingForExistingSession, setIsCheckingForExistingSession] = useState(true);

  const Component = component;
  const oktaAuth = new OktaAuth({
    clientId: oAuth.clientId || import.meta.env.VITE_OAUTH_CLIENT_ID,
    issuer: oAuth.issuer,
    redirectUri: window.location.origin + oAuth.callbackUrl,
    pkce: true,
    scopes: ['email', 'openid', 'profile', 'lpm.exports.create', 'gtms.ecomm.sizechart.read']
  });

  const restoreOriginalUri = async (_oktaAuth, originalUri) => {
    history.replace(toRelativeUrl(originalUri || '/', window.location.origin));
  };

  useEffect(() => {
    oktaAuth.start();

    // If user is logged into Catalogs Tool already, use the same session
    oktaAuth.session.exists().then(exists => {
      const isLoginRedirect = oktaAuth.isLoginRedirect();

      if (exists && !isLoginRedirect) {
        // logged in
        oktaAuth.token
          .getWithoutPrompt({ responseType: 'id_token' })
          .then(res => {
            const { tokens } = res;

            oktaAuth.tokenManager.setTokens(tokens);
          })
          .catch()
          .finally(() => {
            setIsCheckingForExistingSession(false);
          });
      } else {
        setIsCheckingForExistingSession(false);
      }
    });
  }, []);

  const filteredRoutes = useMemo(() => {
    return routes.filter(route => route.section !== 'global.shop' || isShoppingEnabled);
  }, [isShoppingEnabled, routes]);

  return (
    <WithTranslations>
      <ConnectedRouter history={history}>
        <CompatRouter>
          <Suspense fallback={<Spinner large />}>
            <LastLocationProvider>
              {isCheckingForExistingSession ? (
                <Spinner large />
              ) : (
                <Security
                  oktaAuth={oktaAuth}
                  restoreOriginalUri={restoreOriginalUri}
                  onAuthRequired={() => history.push('/login')}
                >
                  <Component>
                    <Switch>
                      <SecureRoute path='/' exact component={HomePage} />
                      <Route path='/login/callback' component={LoginCallback} />
                      {filteredRoutes.map(route =>
                        route.insecure ? (
                          <Route key={route.path} path={route.path} component={route.component} />
                        ) : (
                          <SecureRoute key={route.path} path={route.path} component={route.component} />
                        )
                      )}
                      <Redirect to='/' />
                    </Switch>
                  </Component>
                </Security>
              )}
            </LastLocationProvider>
          </Suspense>
        </CompatRouter>
      </ConnectedRouter>
    </WithTranslations>
  );
};

Routes.defaultProps = {
  history: appHistory,
  oAuth: oAuthDefault
};

Routes.propTypes = {
  ...routerPropTypes
};

const mapStateToProps = state => ({
  isShoppingEnabled: getIsShoppingEnabled(state)
});

const connector = connect(mapStateToProps, {});

export default connector(Routes);
